import { BodyText, Button, Heading } from '@postscript/components';
import { useEffect } from 'react';
import styled from 'styled-components';

import { useUser } from 'controllers/contexts/user';

import RcsGraphic from 'img/rcs-graphic-1.webp';

import {
  PROGRESSIVE_ONBOARDING_SOURCE,
  RCS_FEATURE_KEY,
} from 'providers/ProgressiveOnboarding/constants/constants';

import { logButtonClickEvent, logEvent, PRODUCT_AREAS } from 'utils/events';

import classes from 'components/rcs/onboarding/RcsSplashScreen.module.css';

const RCS_LEARN_MORE_URL = 'https://postscript.io/rcs';

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
`;

const StyledList = styled.ul`
  margin: 0;
  padding-left: var(--spacing-3);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-1);
  padding-top: var(--spacing-2);
`;

export const RcsSplashScreen = ({ dismiss }: { dismiss: () => void }) => {
  const { user } = useUser();

  useEffect(() => {
    logEvent('rcs onboarding splash viewed', {
      productArea: PRODUCT_AREAS.PROGRESSIVE_ONBOARDING,
      source: PROGRESSIVE_ONBOARDING_SOURCE,
      workflow: RCS_FEATURE_KEY,
      shop_id: user.shop_id,
    });
  }, [user.shop_id]);

  const handleLearnMore = (event: React.MouseEvent<HTMLButtonElement>) => {
    window.open(RCS_LEARN_MORE_URL, '_blank');
    logButtonClickEvent(event, {
      productArea: PRODUCT_AREAS.PROGRESSIVE_ONBOARDING,
      source: PROGRESSIVE_ONBOARDING_SOURCE,
      workflow: RCS_FEATURE_KEY,
      shop_id: user.shop_id,
    });
  };

  const handleBegin = (event: React.MouseEvent<HTMLButtonElement>) => {
    logButtonClickEvent(event, {
      productArea: PRODUCT_AREAS.PROGRESSIVE_ONBOARDING,
      source: PROGRESSIVE_ONBOARDING_SOURCE,
      workflow: RCS_FEATURE_KEY,
      shop_id: user.shop_id,
    });
    dismiss();
  };

  return (
    <div className={classes.wrapper}>
      <img src={RcsGraphic} alt="" className={classes.graphic} />

      <StyledBody>
        <Heading size="xx-small">Create RCS Profile</Heading>

        <BodyText size="small">
          Before you can send RCS messages, Google requires a verified sender
          profile for your brand. Your RCS Profile includes your business name,
          logo, and contact details. It&apos;s what subscribers see when they
          receive a message from you.
        </BodyText>

        <BodyText size="small">
          Once you complete your contact card, you&apos;ll be able to test
          sending RCS messages. Once you&apos;re ready to send RCS to your
          subscribers, you&apos;ll submit an RCS sender application.
        </BodyText>

        <BodyText size="small">
          You&apos;ll complete your Profile in minutes. The application process
          typically takes about 4-6 weeks.
        </BodyText>

        <BodyText size="small" bold>
          What you&apos;ll need
        </BodyText>

        {/* TODO: replace stub bullets with final RCS pre-requisites copy */}
        <StyledList>
          <li>
            <BodyText size="small">
              Basic business contact information (name, email, website)
            </BodyText>
          </li>
          <li>
            <BodyText size="small">
              Privacy policy and terms of service links on your site
            </BodyText>
          </li>
          <li>
            <BodyText size="small">
              A logo and banner image that meet RCS spec
            </BodyText>
          </li>
        </StyledList>
      </StyledBody>

      <StyledFooter>
        <Button variant="secondary" onClick={handleLearnMore}>
          Learn More
        </Button>
        <Button variant="primary" onClick={handleBegin}>
          Begin RCS Profile
        </Button>
      </StyledFooter>
    </div>
  );
};
